import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";

import Typography from "@material-ui/core/Typography";

import { GRIDV, GRIDH } from "../admin-settings/table-layout-composer/composer";
import { ConnectedComposerTable } from "./composer-table";
import { OccupiedDay } from "../../api/occupied-days";
import { getSelectedOccupiedDay } from "../../util/general-utility";

interface State {
	gridSize: number;
	sortedTables: Table[];
	locked: boolean;
}

export interface ComposerViewProps {}

interface ConnectedComposerViewProps extends WithStyles<typeof styles> {
	tables: Table[];
	activeAreaId: string;
	selectedDate: Date;
	occupiedDays: OccupiedDay[];
}

type Props = ComposerViewProps &
	ConnectedComposerViewProps &
	InjectedTranslateProps;

export class ComposerView extends React.Component<Props, State> {
	public state: State = {
		gridSize: 0,
		sortedTables: [],
		locked: false
	};

	public componentDidMount() {
		this.updateStateFromProps();
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (
			nextProps.activeAreaId !== this.props.activeAreaId ||
			nextProps.occupiedDays !== this.props.occupiedDays ||
			nextProps.selectedDate !== this.props.selectedDate
		) {
			this.updateStateFromProps(nextProps);
		}
	}

	public shouldComponentUpdate(nextProps: Props, nextState: State) {
		return (
			nextState.locked !== this.state.locked ||
			JSON.stringify(nextState.sortedTables) !==
				JSON.stringify(this.state.sortedTables)
		);
	}

	private updateStateFromProps = (props = this.props) => {
		const { activeAreaId, tables, occupiedDays, selectedDate } = props;

		const sortedTables = tables.filter(t => t.areaId === activeAreaId);
		const occupiedDay = getSelectedOccupiedDay(occupiedDays, selectedDate);
		const lockedAreas = (occupiedDay && occupiedDay.lockedAreas) || [];

		this.setState({
			locked: lockedAreas.indexOf(activeAreaId) > -1,
			sortedTables
		});
	};

	public render() {
		const { classes } = this.props;
		const { gridSize, sortedTables, locked } = this.state;

		return (
			<div className={classes.paper}>
				<div className={classes.composerContainer}>
					<div
						className={classes.composerContent}
						style={{
							paddingBottom: `${(GRIDV / GRIDH) * 100}%`
						}}
					>
						{sortedTables.map(t => (
							<ConnectedComposerTable
								id={t.id}
								key={t.id}
								x={t.layoutX}
								y={t.layoutY}
								width={t.layoutWidth}
								height={t.layoutHeight}
								unitSize={gridSize}
								displayName={t.displayName}
								peopleSoftLimit={t.peopleSoftLimit}
							/>
						))}
					</div>
				</div>
				<div
					className={classNames(classes.lockdownOverlay, {
						[classes.lockdownOverlayVisible]: locked
					})}
				>
					<Typography className={classes.lockdownLabel}>
						Gesperrt
					</Typography>
				</div>
				<div className={classes.legend}>
					{[
						{ icon: classes.legendIconManual, label: "Manuell" },
						{ icon: classes.legendIconBookedOnline, label: "Online Anfrage" },
						{ icon: classes.legendIconPayed, label: "Angezahlt" },
					].map(d => (
						<div key={d.icon} className={classes.legendItem}>
							<div className={classNames(classes.legendIcon, d.icon)} />
							<Typography className={classes.legendLabel}>{d.label}</Typography>
						</div>
					))}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	activeAreaId: state.overviewActiveAreaId,
	tables: state.tables,
	selectedDate: state.date,
	occupiedDays: state.occupiedDays
});

const styles = (theme: Theme) =>
	createStyles({
		paper: {
			background: "#F8F9FA",
			borderRadius: 0,
			position: "relative",
			zIndex: 5,
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			flexGrow: 1,
			boxShadow: "0 0 8px rgba(0, 0, 0, 0.2)",
			paddingBottom: 20
		},
		composerContainer: {
			padding: 0
		},
		composerContent: {
			position: "relative"
		},
		lockdownOverlay: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background: "rgba(255, 255, 255, 0.5)",
			opacity: 0,
			zIndex: 49,
			pointerEvents: "none",
			userSelect: "none",
			transition: "opacity 0.3s ease;"
		},
		lockdownOverlayVisible: {
			opacity: 1,
			pointerEvents: "all"
		},
		lockdownLabel: {
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate3d(-50%, -50%, 0) rotate(-30deg)",
			fontSize: "5rem",
			fontWeight: 500,
			color: "white",
			"-webkit-text-stroke": "2px black"
		},
		legend: {
			position: "absolute",
			bottom: 7,
			left: 0,
			width: "100%",
			display: "flex",
			justifyContent: "center"
		},
		legendItem: {
			padding: "0 10px",
			display: "flex",
			alignItems: "center"
		},
		legendIcon: {
			width: 12,
			height: 12,
			marginRight: 10,
			borderRadius: "100%",
			marginTop: -1
		},
		legendIconManual: {
			backgroundColor: theme.status.reservation.manual
		},
		legendIconBookedOnline: {
			backgroundColor: theme.status.reservation.bookedOnline
		},
		legendIconPayed: {
			backgroundColor: theme.status.reservation.payed
		},
		legendLabel: {
			fontSize: "0.9rem",
			color: "#808080"
		},
	});

export const ConnectedComposerView = translate("translations")(
	withStyles(styles)(connect(mapStateToProps)(ComposerView))
);
